import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import UpgradePromptModal from './UpgradePromptModal/UpgradePromptModal';
import { SubscriptionTier } from '../types/subscription';

interface OtpShare {
  token: string;
  recipientEmail: string;
  createdAt: number;
  expiresAt: number;
  status: 'active' | 'viewed' | 'expired';
  viewedAt: number | null;
}

const OtpSharesPage: React.FC = () => {
  const [shares, setShares] = useState<OtpShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [expirationDays, setExpirationDays] = useState<1 | 3>(3);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [deletingToken, setDeletingToken] = useState<string | null>(null);
  const [userTier, setUserTier] = useState<SubscriptionTier>('Free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeUsage, setUpgradeUsage] = useState({ currentUsage: 0, limit: 1 });

  const loadShares = useCallback(async () => {
    try {
      const data = await apiService.listOtpShares();
      setShares(data.shares);
    } catch (err) {
      console.error('Failed to load secrets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShares();
    apiService.getSubscription().then(res => {
      if (res.subscription?.tier) setUserTier(res.subscription.tier as SubscriptionTier);
    }).catch(() => {});
  }, [loadShares]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreatedUrl('');
    setCopiedUrl(false);

    if (secret.length < 2 || secret.length > 50) {
      setCreateError('Secret must be 2–50 characters.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setCreateError('Enter a valid recipient email address.');
      return;
    }

    setCreating(true);
    try {
      const result = await apiService.createOtpShare(secret, recipientEmail, expirationDays);
      setCreatedUrl(result.viewUrl);
      setSecret('');
      setRecipientEmail('');
      setExpirationDays(3);
      await loadShares();
    } catch (err: any) {
      if (err.statusCode === 403 && err.errorData?.limitReached) {
        setUpgradeUsage({ currentUsage: err.errorData.currentUsage, limit: err.errorData.limit });
        setShowUpgradeModal(true);
      } else {
        setCreateError(err.message || 'Failed to create secret.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (token: string) => {
    setDeletingToken(token);
    try {
      await apiService.deleteOtpShare(token);
      setShares(prev => prev.filter(s => s.token !== token));
    } catch (err) {
      console.error('Failed to delete secret:', err);
    } finally {
      setDeletingToken(null);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const formatDate = (ts: number) => {
    return new Date(ts * 1000).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      active: { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
      viewed: { background: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2' },
      expired: { background: '#f5f5f5', color: '#757575', border: '1px solid #e0e0e0' },
    };
    return (
      <span style={{
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        ...styles[status]
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '0.25rem', fontSize: '1.5rem', fontWeight: 700 }}>Shared Secrets</h2>
      <p style={{ color: 'white', marginBottom: '2rem', fontSize: '14px' }}>
        Safely share sensitive information — such as passwords, access keys, or private notes — with anyone via email.
        Enter your secret and the recipient's email address, and we'll send them a unique private link.
        The link can only be opened <strong>once</strong>: as soon as your recipient views it, the secret is permanently and irreversibly deleted.
      </p>

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
        {/* Create New Secret form — LEFT */}
        <div style={{ flex: '0 0 380px', background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Create New Secret</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '14px', fontWeight: 500 }}>
                Secret <span style={{ color: '#999', fontWeight: 400 }}>({secret.length}/50 characters)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  maxLength={50}
                  placeholder="e.g. MySecretPass123"
                  style={{
                    width: '100%', padding: '0.6rem 2.2rem 0.6rem 0.75rem', borderRadius: '6px',
                    border: '1px solid #d0d0d0', fontSize: '14px', fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(v => !v)}
                  title={showSecret ? 'Hide secret' : 'Show secret'}
                  style={{
                    position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px',
                    color: '#666', padding: '0 2px', lineHeight: 1
                  }}
                >
                  {showSecret ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '14px', fontWeight: 500 }}>
                Recipient Email
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                style={{
                  width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px',
                  border: '1px solid #d0d0d0', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '14px', fontWeight: 500 }}>
                Expires After
              </label>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {([1, 3] as const).map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="radio"
                      name="expiration"
                      value={d}
                      checked={expirationDays === d}
                      onChange={() => setExpirationDays(d)}
                    />
                    {d} {d === 1 ? 'day' : 'days'}
                  </label>
                ))}
              </div>
            </div>

            {createError && (
              <div style={{ color: '#c62828', background: '#ffebee', padding: '0.6rem 0.9rem', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>
                {createError}
              </div>
            )}

            <button
              type="submit"
              disabled={creating}
              style={{
                padding: '0.65rem 1.5rem',
                background: creating ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white', border: 'none', borderRadius: '6px',
                fontSize: '14px', fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer'
              }}
            >
              {creating ? 'Creating…' : 'Create & Send Secret'}
            </button>
          </form>

          {createdUrl && (
            <div style={{ marginTop: '1.25rem', padding: '1rem', background: '#f0f7ff', border: '1px solid #b3d4ff', borderRadius: '8px' }}>
              <div style={{ fontSize: '13px', color: '#1a56b0', fontWeight: 600, marginBottom: '0.5rem' }}>
                Secret created! An email has been sent to the recipient.
              </div>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '0.5rem' }}>
                You can also share this link manually:
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  readOnly
                  value={createdUrl}
                  style={{ flex: 1, padding: '0.4rem 0.6rem', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }}
                />
                <button
                  onClick={() => handleCopyUrl(createdUrl)}
                  style={{
                    padding: '0.4rem 0.75rem', background: copiedUrl ? '#4caf50' : '#667eea',
                    color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {copiedUrl ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Your Secrets list — RIGHT */}
        <div style={{ flex: 1, minWidth: 0, background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Your Secrets</h3>

          {loading ? (
            <div style={{ color: '#999', fontSize: '14px' }}>Loading…</div>
          ) : shares.length === 0 ? (
            <div style={{ color: '#999', fontSize: '14px' }}>No secrets yet. Create your first one on the left.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {shares.map(share => (
                <div key={share.token} style={{
                  padding: '1rem', border: '1px solid #e8e8e8', borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
                      {statusBadge(share.status)}
                      <span style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {share.recipientEmail}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      Created {formatDate(share.createdAt)} · Expires {formatDate(share.expiresAt)}
                      {share.viewedAt && ` · Viewed ${formatDate(share.viewedAt)}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(share.token)}
                    disabled={deletingToken === share.token}
                    style={{
                      padding: '0.35rem 0.75rem', background: 'none', border: '1px solid #e57373',
                      color: '#e57373', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
                      whiteSpace: 'nowrap', flexShrink: 0
                    }}
                  >
                    {deletingToken === share.token ? '…' : 'Delete'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="secrets"
        currentUsage={upgradeUsage.currentUsage}
        limit={upgradeUsage.limit}
        currentTier={userTier}
      />
    </div>
  );
};

export default OtpSharesPage;
